// Terms — AF-1 v1.0 applicant file (sections 1-8) on one application.
import { useState } from "react";
import api from "@/lib/api";

const OPEN = ["received", "missing_info", "verifying", "recommended"];
const today = () => new Date().toISOString().slice(0, 10);
const errText = (e) => {
  const d = e?.response?.data?.detail;
  return Array.isArray(d) ? d.map((x) => x.msg || x.message).join("; ") : d || e.message;
};
const inp = "rounded-md border border-[#cfd4dc] px-2 py-1.5 text-sm";
const btn = "px-4 py-1.5 rounded-full bg-[#0072bc] text-white text-sm font-semibold disabled:opacity-50";
const stamp = (x) => (x ? `${x.by} · ${(x.at || x.date || "").slice(0, 10)}` : null);

function Section({ n, title, meta, children, testId }) {
  return (
    <section className="bg-white border border-[#e5e7eb] rounded-lg p-4 space-y-3" data-testid={testId}>
      <div className="flex items-baseline justify-between gap-3"><h2 className="text-base font-semibold text-[#0e1420]">{n}. {title}</h2>
        {meta && <span className="text-xs text-[#6b7280]">{meta}</span>}</div>
      {children}
    </section>
  );
}

function useSave(appId, slug, onSaved) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const run = async (method, path, body) => {
    setBusy(true); setErr(null);
    try { const { data } = await api.request({ method, url: `/terms/applications/${appId}${path}`, data: body, params: { tenant_slug: slug } }); onSaved(data); }
    catch (e) { setErr(String(errText(e))); } finally { setBusy(false); }
  };
  return { busy, err, run };
}

function Txt({ f, setF, k, label, edit, type = "text", w = "w-44" }) {
  return (
    <label className="text-xs text-[#374151] flex flex-col gap-1">{label}
      <input className={`${inp} ${w}`} type={type} value={f[k] ?? ""} disabled={!edit} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`af1-${k}`} /></label>
  );
}

function YesNo({ f, setF, k, label, edit }) {
  return (
    <label className="text-xs text-[#374151] flex items-center gap-2">
      <select className={inp} value={f[k] === true ? "yes" : f[k] === false ? "no" : ""} disabled={!edit} onChange={(e) => setF({ ...f, [k]: e.target.value === "yes" })} data-testid={`af1-${k}`}>
        <option value="" disabled>—</option><option value="yes">Yes</option><option value="no">No</option>
      </select>{label}</label>
  );
}

function SaveBtn({ edit, save, onClick, testId }) {
  return edit ? <button className={btn} disabled={save.busy} onClick={onClick} data-testid={testId}>Save</button> : null;
}

function Applicant({ rec, edit, save }) {
  const a = rec.af1_applicant;
  const [f, setF] = useState({ company_name: a?.company_name ?? rec.company_name, date_received: a?.date_received ?? (rec.submitted_at || "").slice(0, 10),
    ap_contact_name: a?.ap_contact_name ?? rec.ap_contact_name, ap_phone: a?.ap_phone ?? rec.ap_phone ?? "", ap_email: a?.ap_email ?? rec.ap_email ?? "",
    requested_terms: a?.requested_terms ?? rec.requested_terms ?? 30, est_annual_spend: a?.est_annual_spend ?? rec.est_annual_spend ?? "" });
  const p = { f, setF, edit };
  return (
    <Section n={1} title="Applicant" meta={stamp(a)} testId="af1-applicant">
      <div className="flex gap-3 flex-wrap items-end">
        <Txt {...p} k="company_name" label="Company" w="w-56" /><Txt {...p} k="date_received" label="Date received" type="date" />
        <Txt {...p} k="ap_contact_name" label="AP contact" /><Txt {...p} k="ap_phone" label="Phone" /><Txt {...p} k="ap_email" label="Email" w="w-56" />
        <label className="text-xs text-[#374151] flex flex-col gap-1">Requested terms
          <select className={inp} value={f.requested_terms} disabled={!edit} onChange={(e) => setF({ ...f, requested_terms: Number(e.target.value) })} data-testid="af1-requested_terms"><option value={15}>Net 15</option><option value={30}>Net 30</option></select></label>
        <Txt {...p} k="est_annual_spend" label="Est. annual spend ($)" type="number" />
        <SaveBtn edit={edit} save={save} testId="af1-applicant-save" onClick={() => save.run("put", "/af1/applicant", { ...f, est_annual_spend: f.est_annual_spend === "" ? null : Number(f.est_annual_spend), ap_phone: f.ap_phone || null, ap_email: f.ap_email || null })} />
      </div>
    </Section>
  );
}

function Eligibility({ rec, edit, save }) {
  const el = rec.eligibility;
  const cp = el?.cash_projects || [];
  const [f, setF] = useState({ j1: cp[0]?.job_number || "", d1: cp[0]?.date || "", j2: cp[1]?.job_number || "", d2: cp[1]?.date || "",
    existing_terms_customer: !!el?.existing_terms_customer, payment_history_reviewed: !!el?.payment_history_reviewed, no_late_payments_12mo: !!el?.no_late_payments_12mo });
  const p = { f, setF, edit };
  const projects = [[f.j1, f.d1], [f.j2, f.d2]].filter(([j, d]) => j && d).map(([job_number, date]) => ({ job_number, date }));
  return (
    <Section n={2} title="Eligibility" meta={stamp(el)} testId="af1-eligibility">
      <div className="text-sm" data-testid="terms-eligibility-result">
        {el ? <span className={el.eligible ? "text-[#0e6b52]" : "text-[#b91c1c]"}>Eligible: {el.eligible ? "Yes" : "No"} · {el.count_in_window} cash project(s) in the last {el.window_days} days{el.eligible_by_payment_history ? " · existing terms customer, history OK" : ""}</span> : <span className="text-[#6b7280]">Not recorded yet.</span>}
      </div>
      <div className="flex gap-3 flex-wrap items-end">
        <Txt {...p} k="j1" label="Cash project 1 job #" w="w-32" /><Txt {...p} k="d1" label="Date" type="date" />
        <Txt {...p} k="j2" label="Cash project 2 job #" w="w-32" /><Txt {...p} k="d2" label="Date" type="date" />
      </div>
      <div className="flex gap-4 flex-wrap">
        <YesNo {...p} k="existing_terms_customer" label="Existing terms customer" /><YesNo {...p} k="payment_history_reviewed" label="Payment history reviewed" />
        <YesNo {...p} k="no_late_payments_12mo" label="No late payments in 12 months" />
      </div>
      <SaveBtn edit={edit} save={save} testId="terms-eligibility-save" onClick={() => save.run("put", "/eligibility", { cash_projects: projects,
        existing_terms_customer: f.existing_terms_customer, payment_history_reviewed: f.payment_history_reviewed, no_late_payments_12mo: f.no_late_payments_12mo })} />
    </Section>
  );
}

const REVIEW_FIELDS = [["complete_and_signed", "Complete and signed"], ["all_sections", "All sections"], ["ein_valid", "EIN valid (9 digits)"],
  ["active_mo_sos", "Active in MO SOS"], ["refs_listed_unaffiliated", "Three trade references listed and all unaffiliated"],
  ["red_flag", "Any red flag (family, same address, mismatched name)"]];

function AppReview({ rec, edit, save }) {
  const r = rec.application_review;
  const [f, setF] = useState(Object.fromEntries([...REVIEW_FIELDS.map(([k]) => [k, r ? r[k] : null]), ["notes", r?.notes || ""]]));
  const ready = REVIEW_FIELDS.every(([k]) => typeof f[k] === "boolean");
  return (
    <Section n={3} title="Application review" meta={stamp(r)} testId="af1-application-review">
      {rec.ein_has_9_digits != null && <div className="text-xs text-[#6b7280]" data-testid="af1-ein-hint">EIN on file ({rec.ein}) {rec.ein_has_9_digits ? "has 9 digits" : "does not have 9 digits"}</div>}
      <div className="grid sm:grid-cols-2 gap-2">{REVIEW_FIELDS.map(([k, label]) => <YesNo key={k} f={f} setF={setF} k={k} label={label} edit={edit} />)}</div>
      <Txt f={f} setF={setF} k="notes" label="Notes" edit={edit} w="w-full" />
      <SaveBtn edit={edit && ready} save={save} testId="af1-application-review-save" onClick={() => save.run("put", "/af1/application-review", f)} />
    </Section>
  );
}

function Bank({ rec, edit, save }) {
  const b = rec.bank_check;
  const [f, setF] = useState({ date_called: b?.date_called || today(), spoke_with: b?.spoke_with || "", account_open_since: b?.account_open_since || "",
    avg_balance_range: b?.avg_balance_range || "", result: b?.result || "PASS", notes: b?.notes || "" });
  const p = { f, setF, edit };
  return (
    <Section n={4} title="Bank verification" meta={stamp(b)} testId="terms-review-bank">
      <div className="flex gap-3 flex-wrap items-end">
        <Txt {...p} k="date_called" label="Date called" type="date" /><Txt {...p} k="spoke_with" label="Spoke with" />
        <Txt {...p} k="account_open_since" label="Account open since" w="w-32" /><Txt {...p} k="avg_balance_range" label="Average balance range" />
        <label className="text-xs text-[#374151] flex flex-col gap-1">Result
          <select className={inp} value={f.result} disabled={!edit} onChange={(e) => setF({ ...f, result: e.target.value })} data-testid="terms-bank-result">
            <option value="PASS">PASS</option><option value="LIMITED">LIMITED (Brett decides)</option><option value="FAIL">FAIL</option></select></label>
      </div>
      <Txt {...p} k="notes" label="Bank notes" w="w-full" />
      <SaveBtn edit={edit} save={save} testId="terms-bank-save" onClick={() => save.run("put", "/bank-check", f)} />
    </Section>
  );
}

function RefRow({ i, rec, edit, save }) {
  const c = (rec.ref_checks || [])[i];
  const ref = (rec.trade_refs || [])[i];
  const [f, setF] = useState({ company: c?.company || ref?.company || "", date_called: c?.date_called || today(), spoke_with: c?.spoke_with || "", late_payment: c?.late_payment ?? false, notes: c?.notes || "" });
  const p = { f, setF, edit };
  return (
    <div className="border-t border-[#f1f2f4] pt-3 flex gap-3 flex-wrap items-end" data-testid={`terms-ref-check-${i}`}>
      <Txt {...p} k="company" label={`Reference ${i + 1} company`} w="w-48" /><Txt {...p} k="date_called" label="Date called" type="date" /><Txt {...p} k="spoke_with" label="Spoke with" />
      <YesNo {...p} k="late_payment" label="Any late payment" />
      <span className={`text-xs font-semibold ${c ? (c.pass ? "text-[#0e6b52]" : "text-[#b91c1c]") : "text-[#6b7280]"}`} data-testid={`terms-ref-${i}-pass`}>{c ? (c.pass ? "PASS" : "FAIL (R2)") : "not checked"}</span>
      <SaveBtn edit={edit} save={save} testId={`terms-ref-${i}-save`} onClick={() => save.run("put", `/ref-checks/${i}`, f)} />
    </div>
  );
}

function ReasonSelect({ reasons, value, onChange, testId }) {
  return (
    <select className={inp} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId}>
      <option value="" disabled>Reason code…</option>
      {reasons.map((r) => <option key={r.code} value={r.code}>{`${r.code} — ${r.label}`}</option>)}
    </select>
  );
}

function Recommendation({ rec, edit, save, reasons }) {
  const r = rec.recommendation;
  const [value, setValue] = useState(r?.value || "approve");
  const [reason, setReason] = useState(r?.reason_code || "");
  const [sent, setSent] = useState(r?.date_sent_to_brett || "");
  const label = { approve: "Approve", deny: "Deny", not_yet_eligible: "Not yet eligible" };
  return (
    <Section n={6} title="Recommendation" meta={r && `${r.by} · ${r.date?.slice(0, 10)}`} testId="terms-review-recommendation">
      {r && <div className="text-sm" data-testid="terms-recommendation-current">{label[r.value] || r.value}{r.reason_code ? ` · ${r.reason_code}` : ""}{r.date_sent_to_brett ? ` · sent to Brett ${r.date_sent_to_brett}` : ""}</div>}
      {edit && <div className="flex gap-2 items-end flex-wrap">
        <select className={inp} value={value} onChange={(e) => setValue(e.target.value)} data-testid="terms-recommend-value">
          {Object.entries(label).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        {value === "deny" && <ReasonSelect reasons={reasons} value={reason} onChange={setReason} testId="terms-recommend-reason" />}
        <label className="text-xs flex flex-col gap-1">Date sent to Brett<input className={inp} type="date" value={sent} onChange={(e) => setSent(e.target.value)} data-testid="terms-recommend-sent" /></label>
        <button className={btn} disabled={save.busy || (value === "deny" && !reason)} onClick={() => save.run("post", "/recommend", { value, reason_code: value === "deny" ? reason : null, date_sent_to_brett: sent || null })} data-testid="terms-recommend-submit">Submit recommendation</button>
      </div>}
    </Section>
  );
}

function DecisionForm({ rec, save, reasons }) {
  const rv = rec.recommendation?.value;
  const [value, setValue] = useState(rv === "deny" ? "deny" : "approve_15");
  const [limit, setLimit] = useState("");
  const [reason, setReason] = useState(rec.recommendation?.reason_code || "");
  const [initials, setInitials] = useState("");
  const [date, setDate] = useState(today());
  const ok = initials.trim() && date && (value === "deny" ? !!reason : Number(limit) > 0);
  const body = value === "deny" ? { value, reason_code: reason, initials, date } : { value, credit_limit: Number(limit), initials, date };
  return (
    <div className="flex gap-2 items-end flex-wrap">
      <select className={inp} value={value} onChange={(e) => setValue(e.target.value)} data-testid="terms-decision-value">
        <option value="approve_15">Approve Net 15</option><option value="approve_30">Approve Net 30</option><option value="deny">Deny</option></select>
      {value === "deny" ? <ReasonSelect reasons={reasons} value={reason} onChange={setReason} testId="terms-decision-reason" />
        : <input className={inp} type="number" placeholder="Credit limit ($)" value={limit} onChange={(e) => setLimit(e.target.value)} data-testid="terms-decision-limit" />}
      <input className={`${inp} w-20`} placeholder="Initials" value={initials} onChange={(e) => setInitials(e.target.value)} data-testid="terms-decision-initials" />
      <input className={inp} type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="terms-decision-date" />
      <button className={btn} disabled={save.busy || !ok} onClick={() => save.run("post", "/decision", body)} data-testid="terms-decision-submit">Confirm decision</button>
      {value === "approve_30" && <span className="text-xs text-[#6b7280] w-full">Net 30 needs the DOC-13 review checks recorded (sections 3, 4 and all three references).</span>}
    </div>
  );
}

function Decision({ rec, scope, save, reasons }) {
  const d = rec.decision;
  const own = rec.recommendation?.by === scope.email;
  const names = { approve_15: "Approved · Net 15", approve_30: "Approved · Net 30", deny: "Denied" };
  return (
    <Section n={7} title="Decision" meta={d && `${d.initials} · ${d.date}`} testId="terms-review-decision">
      {d ? (
        <div className="text-sm" data-testid="terms-decision-current">
          {names[d.value] || d.value}{d.credit_limit != null ? ` · limit $${Number(d.credit_limit).toLocaleString()}` : ""}{d.reason_code ? ` · ${d.reason_code} (email ${d.denial_email_template})` : ""}
          {d.reapply_date && <span className="ml-2 text-xs" data-testid="terms-decision-reapply">Eligible to reapply {d.reapply_date}</span>}
          {rec.decision_email_pending && <span className="ml-2 text-xs text-[#a16207]" data-testid="terms-decision-email-pending">Decision email not sent yet</span>}
        </div>
      ) : rec.status !== "recommended" ? <div className="text-xs text-[#6b7280]">Waiting for a recommendation.</div>
        : !scope.can_approve ? <div className="text-xs text-[#6b7280]" data-testid="terms-decision-no-right">Only a Terms approver can give the final decision.</div>
        : own ? <div className="text-xs text-[#a16207]" data-testid="terms-decision-own">You made the recommendation, so a different approver must confirm it.</div>
        : <DecisionForm rec={rec} save={save} reasons={reasons} />}
    </Section>
  );
}

const CLOSE_FIELDS = [["agreement_sent_date", "Agreement sent"], ["agreement_signed_date", "Agreement signed"], ["st_term_set_date", "ServiceTitan term set (date)"],
  ["next_review_date", "Next review"], ["denial_email_sent_date", "Denial email sent"], ["eligible_to_reapply_date", "Eligible to reapply"]];

function CloseOut({ rec, canEdit, save }) {
  const c = rec.close_out;
  const [f, setF] = useState({ ...Object.fromEntries(CLOSE_FIELDS.map(([k]) => [k, c?.[k] || (k === "eligible_to_reapply_date" ? rec.reapply_date || "" : "")])), st_term_set_initials: c?.st_term_set_initials || "" });
  const edit = canEdit && ["approved", "denied"].includes(rec.status);
  const body = Object.fromEntries(Object.entries(f).map(([k, v]) => [k, v || null]));
  return (
    <Section n={8} title="Close-out" meta={stamp(c)} testId="af1-close-out">
      {!["approved", "denied"].includes(rec.status) && <div className="text-xs text-[#6b7280]">Recorded after the decision.</div>}
      <div className="flex gap-3 flex-wrap items-end">
        {CLOSE_FIELDS.map(([k, label]) => <Txt key={k} f={f} setF={setF} k={k} label={label} type="date" edit={edit} />)}
        <Txt f={f} setF={setF} k="st_term_set_initials" label="ST term set (initials)" edit={edit} w="w-24" />
        <SaveBtn edit={edit} save={save} testId="af1-close-out-save" onClick={() => save.run("put", "/af1/close-out", body)} />
      </div>
    </Section>
  );
}

export function TermsReview({ rec, scope, reasons, onSaved }) {
  const save = useSave(rec.id, scope.tenant_slug, onSaved);
  const edit = scope.can_review && OPEN.includes(rec.status);
  return (
    <div className="space-y-4" data-testid="terms-review">
      <div className="text-xs text-[#6b7280]">Applicant file AF-1 v1.0 (DOC-10)</div>
      {!scope.can_review && <div className="text-xs text-[#6b7280]" data-testid="terms-review-readonly">Read-only: you can view this file but not change it.</div>}
      <Applicant rec={rec} edit={edit} save={save} />
      <Eligibility rec={rec} edit={edit} save={save} />
      <AppReview rec={rec} edit={edit} save={save} />
      <Bank rec={rec} edit={edit} save={save} />
      <Section n={5} title="Trade references" testId="terms-review-refs">
        <div className="text-xs text-[#a16207]" data-testid="terms-ref-rule">Rule: zero late payments. A single late payment is a fail (R2).</div>
        {[0, 1, 2].map((i) => <RefRow key={i} i={i} rec={rec} edit={edit} save={save} />)}
      </Section>
      <Recommendation rec={rec} edit={edit} save={save} reasons={reasons} />
      <Decision rec={rec} scope={scope} save={save} reasons={reasons} />
      <CloseOut rec={rec} canEdit={scope.can_review} save={save} />
      {save.err && <div className="text-sm text-[#b91c1c]" data-testid="terms-review-error">{save.err}</div>}
    </div>
  );
}
